import { useSyncExternalStore, useCallback } from 'react'

export type ToastVariant = 'default' | 'success' | 'error' | 'warning'

export interface ToastItem {
  id: string
  title: string
  description?: string
  variant?: ToastVariant
  duration?: number
}

interface State { items: ToastItem[] }

let state: State = { items: [] }
const listeners = new Set<() => void>()
function emit() { listeners.forEach(l => l()) }

export const toastStore = {
  getState: () => state,
  subscribe: (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  add: (item: Omit<ToastItem, 'id'>): string => {
    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    const full: ToastItem = { id, variant: 'default', duration: 4000, ...item }
    state = { items: [...state.items, full] }
    emit()
    if (full.duration && full.duration > 0) {
      setTimeout(() => toastStore.remove(id), full.duration)
    }
    return id
  },
  remove: (id: string) => {
    state = { items: state.items.filter(t => t.id !== id) }
    emit()
  },
}

export function useNotify() {
  const notify = useCallback((item: Omit<ToastItem, 'id'>) => toastStore.add(item), [])
  const dismiss = useCallback((id: string) => toastStore.remove(id), [])
  return { notify, dismiss }
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(toastStore.subscribe, toastStore.getState, toastStore.getState).items
}
